import { useState } from "react"
import { Section, CodeBlock, InfoBox } from "@/components"
import { Zap, Settings } from "lucide-react"
import { cn } from "@/lib/utils"

export function ReactShadcnQuickStartPage() {
  const [setupMode, setSetupMode] = useState<"simple" | "detailed">("simple")

  return (
    <div className="space-y-10">
      <div className="space-y-4">
        <h1 className="text-4xl font-bold">Quick Start</h1>
        <p className="text-lg text-muted-foreground max-w-4xl leading-relaxed">
          Get the React + Shadcn UI template running in minutes. Choose between a simple 
          setup for getting started quickly, or follow the detailed guide for full understanding.
        </p>
      </div>

      <div className="space-y-8">
        <div className="flex gap-4 p-2 bg-muted/30 rounded-xl">
          <button
            onClick={() => setSetupMode("simple")}
            className={cn(
              "flex-1 flex items-center justify-center gap-3 px-8 py-5 rounded-lg font-semibold text-lg transition-all",
              setupMode === "simple"
                ? "bg-primary text-primary-foreground shadow-lg"
                : "bg-transparent hover:bg-muted"
            )}
          >
            <Zap className="h-6 w-6" />
            Simple Setup
          </button>
          <button
            onClick={() => setSetupMode("detailed")}
            className={cn(
              "flex-1 flex items-center justify-center gap-3 px-8 py-5 rounded-lg font-semibold text-lg transition-all",
              setupMode === "detailed"
                ? "bg-primary text-primary-foreground shadow-lg"
                : "bg-transparent hover:bg-muted"
            )}
          >
            <Settings className="h-6 w-6" />
            Detailed Setup
          </button>
        </div>
        
        {setupMode === "simple" && (
          <div className="space-y-6 mt-6">
            <InfoBox type="info" title="Quick Start">
              This is the fastest way to get started. Perfect for exploring the template.
            </InfoBox>
            <CodeBlock
              code={`# Navigate to the template directory
cd react-shadcn-template

# Install dependencies
npm install

# Start development server
npm run dev`}
              language="bash"
            />
            <p className="text-sm text-muted-foreground">
              The application will be available at <code className="bg-muted px-2 py-1 rounded">http://localhost:5173</code>
            </p>
          </div>
        )}
        
        {setupMode === "detailed" && (
          <div className="space-y-6 mt-6">
            <div className="space-y-8">
              <div className="space-y-4">
                <h3 className="text-xl font-semibold">1. Prerequisites</h3>
                <div className="bg-muted/30 rounded-lg p-6">
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex gap-2">
                      <span className="text-primary">→</span>
                      <span><strong>Node.js</strong> 18.x or higher</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-primary">→</span>
                      <span><strong>npm</strong> or <strong>yarn</strong> or <strong>pnpm</strong></span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-primary">→</span>
                      <span>A code editor (VS Code recommended)</span>
                    </li>
                  </ul>
                </div>
              </div>
              
              <div className="space-y-4">
                <h3 className="text-xl font-semibold">2. Navigate to Template</h3>
                <CodeBlock
                  code={`cd react-shadcn-template`}
                  language="bash"
                />
              </div>
              
              <div className="space-y-4">
                <h3 className="text-xl font-semibold">3. Install Dependencies</h3>
                <CodeBlock
                  code={`# Using npm
npm install

# Using yarn
yarn install

# Using pnpm
pnpm install`}
                  language="bash"
                />
                <p className="text-sm text-muted-foreground">
                  This will install React, Vite, Shadcn UI, TypeScript, and all other dependencies.
                </p>
              </div>
              
              <div className="space-y-4">
                <h3 className="text-xl font-semibold">4. Configure Environment (Optional)</h3>
                <p className="text-sm text-muted-foreground">
                  If you need to connect to a backend API, create a <code className="bg-muted px-2 py-1 rounded">.env</code> file:
                </p>
                <CodeBlock
                  code={`VITE_API_URL=http://localhost:3000/api
VITE_APP_NAME=React Shadcn Template`}
                  language="bash"
                />
              </div>
              
              <div className="space-y-4">
                <h3 className="text-xl font-semibold">5. Start Development Server</h3>
                <CodeBlock
                  code={`npm run dev`}
                  language="bash"
                />
                <InfoBox type="success" title="You're all set!">
                  The development server will start with hot module replacement (HMR). 
                  Any changes you make will be reflected instantly.
                </InfoBox>
              </div>
            </div>
          </div>
        )}
      </div>

      <Section title="What's Next?" description="After installation">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Access Your Application</h3>
            <ul className="space-y-3 text-sm text-muted-foreground">
              <li className="flex gap-2">
                <span className="text-primary">→</span>
                <span><strong>Local:</strong> http://localhost:5173</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary">→</span>
                <span><strong>Network:</strong> Available on your local network</span>
              </li>
            </ul>
          </div>
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Explore the Template</h3>
            <ul className="space-y-3 text-sm text-muted-foreground">
              <li className="flex gap-2">
                <span className="text-primary">→</span>
                <span>Check out the architecture and code organization</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary">→</span>
                <span>Browse Shadcn UI components</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary">→</span>
                <span>Explore the example pages and layouts</span>
              </li>
            </ul>
          </div>
        </div>
      </Section>

      <Section title="Adding New Shadcn UI Components" description="Extend your UI library">
        <div className="space-y-6">
          <p className="text-muted-foreground leading-relaxed">
            The template uses Shadcn UI, which allows you to add components as needed. 
            Components are installed directly into your project for full customization.
          </p>
          <CodeBlock
            code={`# Add a single component
npx shadcn-ui@latest add dialog

# Add multiple components at once
npx shadcn-ui@latest add dialog dropdown-menu toast

# List all available components
npx shadcn-ui@latest add`}
            language="bash"
          />
          <InfoBox type="info" title="Component Customization">
            Components are added to <code className="bg-muted px-2 py-1 rounded">src/components/ui/</code> 
            where you can modify them to fit your design needs.
          </InfoBox>
        </div>
      </Section>

      <Section title="Development Workflow" description="Common commands">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-card border rounded-lg p-6 space-y-3">
            <code className="text-sm font-mono text-primary block">npm run dev</code>
            <p className="text-sm text-muted-foreground">
              Start development server with hot module replacement
            </p>
          </div>
          <div className="bg-card border rounded-lg p-6 space-y-3">
            <code className="text-sm font-mono text-primary block">npm run build</code>
            <p className="text-sm text-muted-foreground">
              Build optimized production bundle
            </p>
          </div>
          <div className="bg-card border rounded-lg p-6 space-y-3">
            <code className="text-sm font-mono text-primary block">npm run preview</code>
            <p className="text-sm text-muted-foreground">
              Preview production build locally
            </p>
          </div>
          <div className="bg-card border rounded-lg p-6 space-y-3">
            <code className="text-sm font-mono text-primary block">npm run lint</code>
            <p className="text-sm text-muted-foreground">
              Check code quality with ESLint
            </p>
          </div>
        </div>
      </Section>

      <div className="bg-primary/5 border border-primary/20 rounded-xl p-8 space-y-4">
        <h2 className="text-2xl font-bold">Pro Tips</h2>
        <ul className="space-y-3 text-sm">
          <li className="flex gap-3">
            <span className="text-primary shrink-0">→</span>
            <span>Use <code className="bg-muted px-2 py-1 rounded">npm run dev</code> for instant feedback during development</span>
          </li>
          <li className="flex gap-3">
            <span className="text-primary shrink-0">→</span>
            <span>Install VS Code extensions: ESLint, Prettier, Tailwind CSS IntelliSense</span>
          </li>
          <li className="flex gap-3">
            <span className="text-primary shrink-0">→</span>
            <span>Use <code className="bg-muted px-2 py-1 rounded">@/</code> path alias for cleaner imports</span>
          </li>
          <li className="flex gap-3">
            <span className="text-primary shrink-0">→</span>
            <span>Check out the Shadcn UI documentation for component customization</span>
          </li>
        </ul>
      </div>
    </div>
  )
}
